import { useEffect, useRef } from 'react';

import { SxProps, Theme } from '@mui/material/styles';
import {
    Avatar,
    AvatarProgressProps,
    AvatarVariant,
    ImageSize,
    ImageSizes,
} from '@sage/ui-components';

import useImageCache from 'src/hooks/useImageCache';
import {
    PersonIndicators,
    PersonStatusBadges,
    getBadgeTint,
    getPersonInitials,
    resolvePersonStatus,
} from 'src/utils/personAvatar';

export type { PersonIndicators } from 'src/utils/personAvatar';

export interface PersonProgress {
    /** 0-100. */
    value: number;
    color?: AvatarProgressProps['color'];
}
export interface PersonProps extends PersonIndicators {
    id?: string;
    image?: string;
    useUpdatedImage?: boolean;
    firstName?: string;
    preferredName?: string;
    lastName?: string;
    size?: ImageSize;
    variant?: AvatarVariant;
    /** When true and no image is available yet, a skeleton placeholder is shown. */
    isLoading?: boolean;
    /** Renders a progress ring around the avatar. Omit for no ring. */
    progress?: PersonProgress;
    sx?: SxProps<Theme>;
}

export default function Person(props: PersonProps) {
    const {
        id,
        image,
        useUpdatedImage = false,
        firstName,
        preferredName,
        lastName,
        size = ImageSizes.Medium,
        variant = 'circular',
        isLoading = false,
        progress,
        sx,
    } = props;

    const imageContainerRef = useRef(null);
    const [imageData, { isLoading: isImageLoading, refetch: refetchImage }] = useImageCache(
        id,
        image,
        { imageContainerRef }
    );

    useEffect(() => {
        if (useUpdatedImage || image !== undefined) {
            refetchImage();
        }
    }, [refetchImage, useUpdatedImage, image]);

    const status = resolvePersonStatus(props);
    const badge = status ? PersonStatusBadges[status] : undefined;
    const fallbackText = getPersonInitials({ firstName, preferredName, lastName });

    return (
        <Avatar
            size={size}
            variant={variant}
            {...(imageData
                ? { src: imageData, fallbackText, alt: `${firstName} ${lastName}` }
                : { fallbackText })}
            isLoading={isLoading || isImageLoading}
            imageRef={imageContainerRef}
            badge={
                badge && {
                    ...badge,
                    size,
                    variant,
                    color: getBadgeTint(status, progress?.color) ?? badge.color,
                }
            }
            progress={progress && { ...progress, size }}
            sx={sx}
            data-testid="person-avatar"
        />
    );
}
